import React from 'react';
import { Helmet } from 'react-helmet-async';

export type JsonLd = Record<string, unknown>;

export interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  noindex?: boolean;
  image?: string;
  imageAlt?: string;
  schemaType?: 'WebPage' | 'CollectionPage' | 'AboutPage';
  breadcrumbs?: BreadcrumbItem[];
  jsonLd?: JsonLd | JsonLd[];
}

export const SITE_URL = 'https://www.tcgcalendar.com.au';
export const SITE_NAME = 'TCG Calendar Australia';
const DEFAULT_TITLE = 'TCG Calendar Australia - Trading Card Game Events & Tournaments';
const DEFAULT_DESCRIPTION = 'Find Pokemon, One Piece, Magic: The Gathering and trading card game events across Australia. Browse upcoming tournaments, league events, prereleases and competitions near you.';
const DEFAULT_IMAGE = `${SITE_URL}/tcg-calendar-social.png`;
const BRAND_LOGO = `${SITE_URL}/tcg-calendar-brand-logo.png`;
const DEFAULT_IMAGE_ALT = 'TCG Calendar Australia logo';

const normalizePath = (path: string) => {
  if (!path || path === '/') return '/';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return cleanPath.replace(/\/+$/, '');
};

export const absoluteUrl = (pathOrUrl: string) => {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${normalizePath(pathOrUrl)}`;
};

const buildBreadcrumbJsonLd = (breadcrumbs: BreadcrumbItem[]): JsonLd | null => {
  if (breadcrumbs.length < 2) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
};

const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  noindex = false,
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  schemaType = 'WebPage',
  breadcrumbs = [],
  jsonLd,
}) => {
  const pageTitle = title ? `${title} | TCG Calendar Australia` : DEFAULT_TITLE;
  const canonicalUrl = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);
  const usesDefaultShareImage = imageUrl === DEFAULT_IMAGE;
  const baseJsonLd: JsonLd = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: pageTitle,
    url: canonicalUrl,
    description,
    inLanguage: 'en-AU',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: BRAND_LOGO,
    },
  };
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(breadcrumbs);
  const extraJsonLd = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const structuredData = [baseJsonLd, breadcrumbJsonLd, ...extraJsonLd].filter(Boolean) as JsonLd[];

  return (
    <Helmet>
      <html lang="en-AU" />
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
      />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en-AU" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt} />
      {usesDefaultShareImage && <meta property="og:image:width" content="1200" />}
      {usesDefaultShareImage && <meta property="og:image:height" content="630" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {structuredData.map((item, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
